import * as React from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { useToggle } from "react-use";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  useExecuteColumnAnalysisMutation,
  useSaveColumnAnalysisMutation,
} from "@/modules/snapshots/businessReports.api";
import { StreamingArtefactMarkdown } from "@/components/feature/markdown/StreamingArtefactMarkdown";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { MESSAGE_STYLES } from "@/components/chat/constants";
import { ScrollableChat } from "@/components/feature/chat/ScrollableChat";
import { ArrowLeft, ChevronDown, ChevronRight, Pencil, Play, Save } from "lucide-react";
import { useColumnAnalysisStream } from "./hooks/useColumnAnalysisStream";
import { useArtefactExtraction } from "@/hooks/useArtefactExtraction";
import { EditPromptDialog } from "./EditPromptDialog";

interface Props {
  businessReportId: string;
  snapshotCollectionId: string;
  subcollectionId?: string;
  fieldName: string;
  prompt: string;
  initialResult?: string | null;
  onBack: () => void;
  onSaved: () => void;
  contractFamilyProjection?: boolean;
}

export const AnalysisResult: React.FC<Props> = ({
  businessReportId,
  snapshotCollectionId,
  subcollectionId,
  fieldName,
  prompt: initialPrompt,
  initialResult = null,
  onBack,
  onSaved,
  contractFamilyProjection = false,
}) => {
  const [prompt, setPrompt] = React.useState(initialPrompt);
  const [isEditOpen, toggleEditOpen] = useToggle(false);
  const [isPromptCollapsed, togglePromptCollapsed] = useToggle(!!initialResult);
  const [conversationId, setConversationId] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<string | null>(initialResult);
  const [isExecuting, setIsExecuting] = React.useState(false);
  const [executeAnalysis] = useExecuteColumnAnalysisMutation();
  const [saveAnalysis, { isLoading: isSaving }] = useSaveColumnAnalysisMutation();

  React.useEffect(() => {
    setPrompt(initialPrompt);
  }, [initialPrompt]);

  const handleComplete = React.useCallback((content: string) => {
    setResult(content);
    setIsExecuting(false);
  }, []);

  const { content: streamContent, isStreaming } = useColumnAnalysisStream({
    conversationId,
    onComplete: handleComplete,
  });

  const handleExecute = async () => {
    const newConversationId = crypto.randomUUID();
    setConversationId(newConversationId);
    setResult(null);
    setIsExecuting(true);
    if (!isPromptCollapsed) togglePromptCollapsed();

    try {
      await executeAnalysis({
        business_report_id: businessReportId,
        snapshot_collection_id: snapshotCollectionId,
        subcollection_id: subcollectionId ?? null,
        field_name: fieldName,
        prompt,
        conversation_id: newConversationId,
        contract_family_projection: contractFamilyProjection,
      }).unwrap();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to execute analysis. Please try again.");
      setConversationId(null);
      setIsExecuting(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;

    try {
      await saveAnalysis({
        business_report_id: businessReportId,
        snapshot_collection_id: snapshotCollectionId,
        subcollection_id: subcollectionId ?? null,
        field_name: fieldName,
        prompt,
        result,
        contract_family_projection: contractFamilyProjection,
      }).unwrap();
      onSaved();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to save analysis. Please try again.");
    }
  };

  const displayContent = isStreaming ? streamContent : result;
  const [processedContent, inlineArtefacts] = useArtefactExtraction(displayContent ?? "");

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <button className="flex items-center gap-1" onClick={togglePromptCollapsed}>
          {isPromptCollapsed ? (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
          <h3 className="text-lg font-semibold">Analysis Prompt</h3>
        </button>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {isPromptCollapsed ? (
          <motion.button
            key="collapsed"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="truncate rounded-lg border border-desert-sand-400 bg-ivory-mist-100 px-3 py-2 text-left text-sm leading-relaxed text-foreground"
            onClick={togglePromptCollapsed}
          >
            {prompt.split("\n")[0]} ...
          </motion.button>
        ) : (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="max-h-[40vh] overflow-y-auto rounded-lg border border-desert-sand-400 bg-ivory-mist-100 px-3 py-2 text-sm leading-relaxed text-foreground"
          >
            <StreamingArtefactMarkdown content={prompt} artefacts={[]} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={toggleEditOpen}>
          <Pencil className="mr-2 h-4 w-4" />
          Edit Prompt
        </Button>
        <Button onClick={handleExecute} disabled={isExecuting || !prompt.trim()}>
          <Play className="mr-2 h-4 w-4" />
          Execute
        </Button>
      </div>

      {displayContent != null && (
        <div className="min-h-0 flex-1">
          <ScrollableChat className="px-1">
            <div className={MESSAGE_STYLES.ai}>
              <StreamingArtefactMarkdown content={processedContent} artefacts={inlineArtefacts} />
              <AnimatePresence>
                {isStreaming && (
                  <div className="text-coral">
                    <TypingIndicator />
                  </div>
                )}
              </AnimatePresence>
            </div>
          </ScrollableChat>
        </div>
      )}

      {result && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="mr-2 h-4 w-4" />
            Save
          </Button>
        </div>
      )}

      <EditPromptDialog open={isEditOpen} onOpenChange={toggleEditOpen} prompt={prompt} onSave={setPrompt} />
    </div>
  );
};
